import { createFileRoute, Link } from '@tanstack/react-router'
import { useI18n } from '@/i18n/i18n-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { LoadingSpinner } from '@/components/loading-spinner'
import { Calendar, ReceiptText, ArrowLeft, Store } from 'lucide-react'
import { useGetCustomerOrders } from '@/api/react-query'
import { ErrorDisplay } from '@/components/error-display'
import { Helmet } from 'react-helmet-async'

export const Route = createFileRoute('/receipts')({
	component: ReceiptsList
})

function ReceiptsList() {
	const { L } = useI18n()

	const receipts = useGetCustomerOrders()

	if (receipts.isLoading) {
		return (
			<div className='container mx-auto max-w-4xl p-4 sm:p-6'>
				<div className='flex items-center justify-center py-12'>
					<LoadingSpinner />
				</div>
			</div>
		)
	}

	if (receipts.error) {
		return (
			<div className='container mx-auto max-w-4xl p-4 sm:p-6'>
				<div className='flex items-center gap-3 pb-6 sm:pb-8'>
					<ReceiptText className='text-primary h-6 w-6 sm:h-8 sm:w-8' />
					<h1 className='text-2xl font-bold sm:text-3xl lg:text-4xl'>{L.Receipts.Title()}</h1>
				</div>
				<Card className='p-6 sm:p-8 lg:p-12'>
					<ErrorDisplay refetch={receipts.refetch} className='space-y-4' />
				</Card>
				<Button asChild variant='outline' size='sm' className='mt-4'>
					<Link to='/'>
						<ArrowLeft className='mr-2 h-4 w-4' />
						{L.Receipts.BackToHome()}
					</Link>
				</Button>
			</div>
		)
	}

	return (
		<div className='container mx-auto max-w-4xl p-4 sm:p-6'>
			<Helmet>
				<title>{L.Receipts.Title()}</title>
			</Helmet>
			<div className='flex items-center gap-3 pb-6 sm:pb-8'>
				<ReceiptText className='text-primary h-6 w-6 sm:h-8 sm:w-8' />
				<h1 className='text-2xl font-bold sm:text-3xl lg:text-4xl'>{L.Receipts.Title()}</h1>
			</div>

			{receipts.data?.length === 0 ?
				<Card className='p-6 sm:p-8 lg:p-12'>
					<div className='space-y-4 text-center'>
						<ReceiptText className='text-muted-foreground/50 mx-auto h-12 w-12 sm:h-16 sm:w-16' />
						<p className='text-muted-foreground text-lg sm:text-xl'>{L.Receipts.Empty()}</p>
						<Button asChild size='lg'>
							<Link to='/'>{L.Cart.ContinueShopping()}</Link>
						</Button>
					</div>
				</Card>
			:	<div className='space-y-3 sm:space-y-4'>
					{receipts.data?.map(receipt => (
						<Card key={receipt.uuid} className='group transition-shadow hover:shadow-lg'>
							<Link to='/receipt/$receiptId' params={{ receiptId: receipt.uuid }}>
								<div className='flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-6'>
									<div className='space-y-1'>
										<h3 className='text-lg font-semibold'>
											{L.Receipts.OrderNumber(
												receipt.displayId?.toString() || L.Receipts.Unknown()
											)}
										</h3>
										<div className='text-muted-foreground flex flex-col gap-1 text-sm sm:flex-row sm:gap-4'>
											<div className='flex items-center gap-1'>
												<Calendar className='h-4 w-4' />
												{L.Date.DateAndTime(receipt.completedAt)}
											</div>
											<div className='flex items-center gap-1'>
												<Store className='h-4 w-4' />
												{receipt.salesChannel.name}
											</div>
										</div>
									</div>

									<div className='flex items-center justify-between gap-4 sm:justify-end'>
										<div className='text-right'>
											<p className='text-muted-foreground text-sm'>{L.Receipt.Total()}</p>
											<p className='text-primary text-xl font-bold'>
												{L.Amount.Default({
													amount: receipt.total.gross,
													currencyCode: receipt.currencyCode
												})}
											</p>
										</div>
										<Button variant='outline' className='shrink-0' size='sm'>
											{L.Receipts.ViewReceipt()}
										</Button>
									</div>
								</div>
							</Link>
						</Card>
					))}
				</div>
			}

			<div className='mt-6 flex justify-center sm:mt-8'>
				<Button asChild variant='outline' size='lg' className='w-full sm:w-auto'>
					<Link to='/'>
						<ArrowLeft className='mr-2 h-4 w-4' />
						{L.Receipts.BackToHome()}
					</Link>
				</Button>
			</div>
		</div>
	)
}

export default ReceiptsList
